import loadable from '@loadable/component';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { PageContainer } from '../../../components/common-components/page/page-container/page-container.component';
import { type ProductPageType } from '../../../components/common-components/page/page-container/page-container.types';
import { AddToCartButtonFactory } from '../../../components/product-components/add-to-cart-button/add-to-cart-button.component';
import { ProductBundles } from '../../../components/product-components/product-bundles/product-bundles.component';
import { ProductStickyNav } from '../../../components/product-components/product-sticky-nav/product-sticky-nav.component';
import { ERROR_ADD_ITEMS, PDP_BUYSECTION_ID, PRODUCT_CONFIG_SECTION_ID } from '../../../constants/product';
import { AddToCartContext } from '../../../contexts/add-to-cart/add-to-cart.context';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { AnalyticsHelper, ANALYTICS_PAGE_NAMES } from '../../../helpers/analytics/analytics.helper';
import { ADD_TO_CART_BUTTON_TYPE } from '../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { isConfigurationValid } from '../../../helpers/product-helper/configuration/product-configuration.helper';
import { buildGAConfigureClick } from '../../../helpers/product-helper/product-analytics.helper';
import {
	convertRegionalStatusToAvailabilityEnum,
	createProductName,
	createProductPageTitle,
	isShowroomOnlyProduct
} from '../../../helpers/product-helper/product.helper';
import { useAddToCartButton } from '../../../hooks/add-to-cart/add-to-cart-button.hooks';
import { useCustomAddProductToCart } from '../../../hooks/add-to-cart/add-to-cart.hooks';
import { useSiteViewPreference } from '../../../hooks/apollo/employee/employee.hooks';
import { DyRecommendationContextType, useDyRecommendationContext } from '../../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useCustomerZipCodeWatcher } from '../../../hooks/general/location-watcher.hooks';
import { useNotification } from '../../../hooks/notification/notification.hooks';
import { useSendMetaTrackingPixel } from '../../../hooks/tracking-pixel/tracking-pixel.hooks';
import { useProductStickyNavStore } from '../../../store/product-sticky-nav/product-sticky-nav.store';
import { type PdpProductFamily } from '../../../types/product.types';
import { scrollToElement } from '../../../utils/dom/dom.utils';
import { PdpBuySection } from './components/pdp-buy-section/pdp-buy-section.component';
import { ProductDetailsSection } from './components/product-details-section/product-details-section.component';
import { hideSlideInAnimationNavBanner, slideInAnimationNavBanner } from './product-details.css';

// dynamically import internal info to reduce bundle size for non-internal users
const LoadableInternalProductInfo = loadable(
	() =>
		import(
			/* webpackChunkName: "internalproductinformation" */ '../../../components/product-components/internal-product-information/internal-product-information.component'
		),
	{
		resolveComponent: ({ InternalProductInformation }) => InternalProductInformation
	}
);

const LoadableShowroomContent = loadable(
	() =>
		import(
			/* webpackChunkName: "showroomproductinformation" */ '../../../components/showroom-product-information/showroom-product-information.component'
		),
	{
		resolveComponent: ({ ShowroomProductInformation }) => ShowroomProductInformation
	}
);

type ProductDetailPageProps = {
	product: PdpProductFamily;
	productUid: number;
	refetchData: () => Promise<any>;
	nonStockMode: boolean;
};

export const ProductDetailPage: FunctionComponent<ProductDetailPageProps> = ({ product, productUid, refetchData, nonStockMode }) => {
	const [isAddingToCart, setIsAddingToCart] = useState(false);
	const [findShowroomIsInView, setFindShowroomIsInView] = useState(true);

	const { selectedVariant, currentVariant } = useContext(VariantContext);
	const { selection } = useContext(AddToCartContext);

	const currentActiveSection = useProductStickyNavStore((state) => state.currentActiveSection);
	const setCurrentActiveSection = useProductStickyNavStore((state) => state.setCurrentActiveSection);
	const shouldShowATC = useProductStickyNavStore((state) => state.shouldShowATC);

	const { ref: buysectionRef, inView: buysectionInView } = useInView();
	const { ref: showStickyNavRef, inView: showStickyNavInView } = useInView({ triggerOnce: true });

	const { notifyDanger } = useNotification();
	const { showAsEmployee } = useSiteViewPreference();
	const shouldDisplayShowroomContent = isShowroomOnlyProduct(selectedVariant);

	const sendMetaTrackingPixel = useSendMetaTrackingPixel();

	const configurationValid =
		selection && selection?.quantity === 0 ? false : selection ? isConfigurationValid(selection) : Boolean(selection);

	const cartQuantity = selection?.quantity || 1;

	const brandName = product.brandInfo.name;
	const metaDescription = `Save on the ${brandName} ${currentVariant.modelNumber} from Build.com. Low Prices + Fast & Free Shipping on Most Orders. Find reviews, expert advice, manuals, specs & more.`;
	const pageTitle = createProductPageTitle(brandName, currentVariant.modelNumber, product.title);

	const breadcrumbs = [...product.breadcrumbs, { name: `${brandName} ${product.productId}` }];

	const { buttonType, addToCartActionStatus } = useAddToCartButton({
		pageName: ANALYTICS_PAGE_NAMES.PRODUCT_DETAILS_PAGE
	});

	const AddToCartButton: JSX.Element = (
		<AddToCartButtonFactory
			buttonType={buttonType}
			addToCartButtonType={ADD_TO_CART_BUTTON_TYPE.STICKY}
			handleOnClick={() => onAddToCartClick(ADD_TO_CART_BUTTON_TYPE.STICKY)}
			isConfigurable={product.isConfigurable}
			disabled={!configurationValid}
			status={addToCartActionStatus}
			isBusy={isAddingToCart}
		/>
	);

	const [addToCart, resetAddToCart, addToCartStatus] = useCustomAddProductToCart({
		variantId: selectedVariant.id,
		quantity: cartQuantity,
		validate: isConfigurationValid
	});

	function onAddToCartClick(addToCartButtonType = ADD_TO_CART_BUTTON_TYPE.DEFAULT) {
		if (configurationValid) {
			if (selection) {
				setIsAddingToCart(true);
				addToCart(selection, addToCartButtonType)
					.catch(() => notifyDanger(ERROR_ADD_ITEMS))
					.finally(() => setIsAddingToCart(false));
			}
		} else {
			AnalyticsHelper.track(buildGAConfigureClick()).catch(logError);
			scrollToElement(PRODUCT_CONFIG_SECTION_ID, 55);
		}
	}

	// Variant shipping info depends on customer zip - so refetch if zip changes
	useCustomerZipCodeWatcher(() => {
		void refetchData();
	});

	useDyRecommendationContext({ type: DyRecommendationContextType.PRODUCT_PAGE, data: [`${selectedVariant.id}`] });

	useEffect(() => {
		void sendMetaTrackingPixel(
			'ViewContent',
			selectedVariant.priceInfo.current,
			'product',
			[selectedVariant.id.toString()],
			brandName,
			product.businessCategory.name
		);
	}, []);

	useEffect(() => {
		if (buysectionInView && currentActiveSection) {
			setCurrentActiveSection(PDP_BUYSECTION_ID);
		}
	}, [buysectionInView]);

	return (
		<>
			<PageContainer<ProductPageType>
				brandName={brandName}
				canonicalURL={product.url}
				imagePublicId={currentVariant.image.id}
				isInStock={true}
				itemAvailability={convertRegionalStatusToAvailabilityEnum(String(selectedVariant.regionalStatus))}
				metaDescription={metaDescription}
				modelNumber={currentVariant.modelNumber}
				pageTitle={pageTitle}
				price={currentVariant.priceInfo.current}
				productDescription={product.title}
				productId={`${product.id}`}
				productName={createProductName(brandName, currentVariant.modelNumber)}
				productUrl={currentVariant.url}
				ratingCount={product.reviewSummary.reviewCount}
				ratingValue={product.reviewSummary.ratingValue}
				breadcrumbs={breadcrumbs}
				showUpdateLocation={true}>
				<>
					{/* Empty div to conditionally render sticky nav */}
					<div ref={showStickyNavRef}></div>

					{/* Product Section */}
					<section data-testid="product-details-product-section" id={PDP_BUYSECTION_ID} ref={buysectionRef}>
						{/* TODO: clean up old experience SODEV-40175 */}
						{shouldDisplayShowroomContent ? (
							<LoadableShowroomContent
								product={product}
								productUid={productUid}
								currentVariant={currentVariant}
								currentVariantCountAvailable={currentVariant.count}
								onFindShowroomIsInView={setFindShowroomIsInView}
							/>
						) : (
							<PdpBuySection
								product={product}
								productUid={productUid}
								nonStockMode={nonStockMode}
								familyId={product.id}
								isAddingToCart={isAddingToCart}
								onAddToCartClick={onAddToCartClick}
								resetAddToCart={resetAddToCart}
								addToCartStatus={addToCartStatus}
							/>
						)}
						{/* Bundle Items Panel Section */}
						{!shouldDisplayShowroomContent && (
							<div className="ma0 ma2-ns">
								<ProductBundles bundleItems={currentVariant.bundleItems} />
							</div>
						)}
					</section>

					{/* Recs */}
					<section className="mb6-ns dn-p">
						<div id="dy-recs-product-page-1" />
					</section>

					{/* Detail Section */}
					<section>
						<div className="flex">
							<ProductDetailsSection product={product} familyId={product.id} />
						</div>
					</section>

					{showAsEmployee && (
						<div className="ma2 dn db-ns">
							<LoadableInternalProductInfo familyId={product.id} />
						</div>
					)}
				</>
			</PageContainer>

			{/* STICKY NAV SLIDE IN ANIMATION is productionalized for all PDP users */}
			{/*	This is outside PageContainer so that it will span entire window screen width */}
			{showStickyNavInView && (
				<div
					className={`dn-p z-2 w-100 ${shouldShowATC ? slideInAnimationNavBanner : hideSlideInAnimationNavBanner}`}
					data-testid="sticky-nav">
					<ProductStickyNav
						AddToCartButton={AddToCartButton}
						shouldDisplayShowroomButton={shouldDisplayShowroomContent}
						findShowroomIsInView={findShowroomIsInView}
						currentActiveSection={currentActiveSection}
						removePartsSection={!product.hasReplacementParts}
						restrictionState={selectedVariant.saleRestrictions?.state.name || undefined}
					/>
				</div>
			)}
		</>
	);
};
